/**
 * SongStarRating — premium Songwriter Square 5-star control.
 * Metallic gold outline stars → fill on hover (1..n) → click saves.
 * Anyone can rate (guests via anonymous cookie). Demo tracks persist locally.
 *
 * Props: song {id, slug, rating?}, size "sm" | "md" | "lg", showHelper, align
 */
import React, { useEffect, useId, useState } from "react";
import { useSongRating } from "./ratingStore";

const SIZES = {
  sm: { star: "h-4 w-4", gap: "gap-0.5", text: "text-[9px]" },
  md: { star: "h-6 w-6", gap: "gap-1", text: "text-[10px]" },
  lg: { star: "h-9 w-9 md:h-11 md:w-11", gap: "gap-1.5 md:gap-2", text: "text-[11px] md:text-xs" },
};

const STAR_PATH = "M12 2.6l2.83 6.07 6.63.74-4.9 4.53 1.32 6.56L12 17.2l-5.88 3.3 1.32-6.56-4.9-4.53 6.63-.74z";

function Star({ filled, hot, className, gid }) {
  return (
    <svg viewBox="0 0 24 24" className={`${className} ss-star ${filled ? "ss-star-filled" : ""} ${hot ? "ss-star-hot" : ""}`} aria-hidden="true">
      <defs>
        <linearGradient id={`${gid}-fill`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff3c4" />
          <stop offset="0.35" stopColor="#f2d27a" />
          <stop offset="0.6" stopColor="#d4af37" />
          <stop offset="1" stopColor="#8a6a1c" />
        </linearGradient>
        <linearGradient id={`${gid}-rim`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f6e3a1" />
          <stop offset="1" stopColor="#a67c1e" />
        </linearGradient>
        <linearGradient id={`${gid}-sheen`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="rgba(255,255,255,0)" />
          <stop offset="0.5" stopColor="rgba(255,255,255,0.55)" />
          <stop offset="1" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>
      <path d={STAR_PATH} fill={filled ? `url(#${gid}-fill)` : "rgba(8,8,8,0.85)"} stroke={`url(#${gid}-rim)`} strokeWidth="1.15" strokeLinejoin="round" />
      {filled && <path d={STAR_PATH} fill={`url(#${gid}-sheen)`} className="ss-star-shimmer" style={{ mixBlendMode: "screen" }} />}
    </svg>
  );
}

export default function SongStarRating({ song, size = "md", showHelper = true, align = "start", local = false, className = "" }) {
  const key = local ? `demo:${song?.id}` : (song?.slug || song?.id);
  const [state, rate] = useSongRating(key, local ? null : song?.rating, { local });
  const [hover, setHover] = useState(0);
  const [flash, setFlash] = useState(null);
  const gid = useId().replace(/:/g, "");
  const S = SIZES[size] || SIZES.md;
  const mine = state.my_rating || 0;
  const shown = hover || mine;

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 1600);
    return () => clearTimeout(t);
  }, [flash]);

  const onPick = async (v) => {
    const res = await rate(v);
    setFlash(res.ok ? "RATING SAVED" : "TRY AGAIN");
  };

  const helper = flash
    ? flash
    : mine ? `YOUR RATING: ${mine}/5` : (size === "sm" ? "RATE THIS SONG" : "PLEASE RATE THIS SONG");

  return (
    <div data-testid="song-star-rating" className={`inline-flex flex-col ${align === "center" ? "items-center" : "items-start"} ${className}`}
         onMouseLeave={() => setHover(0)}>
      <div role="radiogroup" aria-label="Rate this song" className={`flex items-center ${S.gap}`}>
        {[1, 2, 3, 4, 5].map((v) => (
          <button key={v} type="button" role="radio" aria-checked={mine === v} aria-label={`${v} star${v > 1 ? "s" : ""}`}
                  data-testid={`star-${v}`} data-filled={v <= shown ? "true" : "false"}
                  onMouseEnter={() => setHover(v)} onFocus={() => setHover(v)} onBlur={() => setHover(0)}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPick(v); }}
                  className="ss-star-btn p-0 bg-transparent border-0 cursor-pointer leading-none">
            <Star filled={v <= shown} hot={hover > 0 && v <= hover} className={S.star} gid={`${gid}s${v}`} />
          </button>
        ))}
      </div>
      {showHelper && (
        <div data-testid="star-helper" className={`mt-1 font-display uppercase tracking-[0.3em] ${S.text} ${flash ? "text-[#f6e3a1]" : mine ? "text-gold/90" : "text-white/45"} transition-colors`}>
          {helper}
          {!flash && state.rating_count > 0 && size !== "sm" && (
            <span className="ml-2 text-white/35 tracking-widest">· {state.average_rating?.toFixed(1)} avg · {state.rating_count}</span>
          )}
        </div>
      )}
    </div>
  );
}
